import { useCallback, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export function Lightbox({ images, index, onClose, onIndexChange }) {
  const isOpen = index !== null && index !== undefined;

  const prev = useCallback(() => {
    onIndexChange((index - 1 + images.length) % images.length);
  }, [index, images.length, onIndexChange]);
  const next = useCallback(() => {
    onIndexChange((index + 1) % images.length);
  }, [index, images.length, onIndexChange]);

  useEffect(() => {
    if (!isOpen) return;
    const lenis = window.__lenis;
    lenis?.stop();
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      lenis?.start();
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose, prev, next]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          data-testid="lightbox"
          className="fixed inset-0 z-[120] bg-nero/95 backdrop-blur-sm flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <button
            data-testid="lightbox-close"
            onClick={onClose}
            className="absolute top-5 right-5 h-11 w-11 rounded-full border border-panna/25 text-panna flex items-center justify-center hover:bg-panna/10 transition-colors"
            aria-label="Chiudi"
          >
            <X size={20} />
          </button>
          {images.length > 1 && (
            <button
              data-testid="lightbox-prev"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-4 sm:left-8 h-12 w-12 rounded-full border border-panna/25 text-panna flex items-center justify-center hover:bg-panna/10 transition-colors"
              aria-label="Precedente"
            >
              <ChevronLeft size={22} />
            </button>
          )}
          <motion.img
            key={index}
            src={images[index].src}
            alt={images[index].alt}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] max-w-[90vw] rounded-xl shadow-2xl object-contain"
            initial={{ scale: 0.94, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.25 }}
          />
          {images.length > 1 && (
            <button
              data-testid="lightbox-next"
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-4 sm:right-8 h-12 w-12 rounded-full border border-panna/25 text-panna flex items-center justify-center hover:bg-panna/10 transition-colors"
              aria-label="Successiva"
            >
              <ChevronRight size={22} />
            </button>
          )}
          {images.length > 1 && (
            <span className="absolute bottom-6 left-1/2 -translate-x-1/2 text-panna/70 text-xs uppercase tracking-[0.3em]">
              {index + 1} / {images.length}
            </span>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
